import { useMemo } from 'react';

import {
  Avatar,
  Box,
  ButtonBase,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Popover,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Brightness7Icon from '@material-ui/icons/Brightness7';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import FontDownloadOutlinedIcon from '@material-ui/icons/FontDownloadOutlined';
import GroupIcon from '@material-ui/icons/Group';
import LockIcon from '@material-ui/icons/Lock';
import { Rating } from '@material-ui/lab';
import { useConfirm } from 'material-ui-confirm';
import {
  bindPopover,
  bindTrigger,
  usePopupState,
} from 'material-ui-popup-state/hooks';
import { useModal } from 'react-modal-hook';

import useAuth from 'hooks/useAuth';
import useCurrentUser from 'hooks/useCurrentUser';

import FontResize from './components/FontResize';
import ModalAlterarSenha from './components/ModalAlterarSenha';
import ModalMinhasEquipes from './components/ModalMinhasEquipes';
import ToggleDistribuicaoAutomaticaFila from './components/ToggleDistribuicaoAutomaticaFila';
import ToggleTheme from './components/ToggleTheme';
import UploadAvatarProfile from './components/UploadAvatarProfile';

const useStyles = makeStyles(({ spacing }) => ({
  popover: {
    width: spacing(40),
    padding: spacing(0),
    paddingTop: spacing(1),
  },
}));

export default function Profile() {
  const classes = useStyles();
  const confirm = useConfirm();
  const currentUser = useCurrentUser();
  const auth = useAuth();

  const totalAvaliacoes = useMemo(() => {
    return currentUser?.avaliacoes?.total_avaliacoes ?? 0;
  }, [currentUser?.avaliacoes?.total_avaliacoes]);

  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'profilePopover',
  });

  const handleConfirmSignOut = () => {
    confirm({
      title: <Typography variant="body2">Sair</Typography>,
      description: (
        <Typography variant="caption">Deseja mesmo sair?</Typography>
      ),
      cancellationText: 'Cancelar',
      confirmationText: 'Sair',
      confirmationButtonProps: { color: 'secondary', variant: 'contained' },
      dialogProps: { maxWidth: 'xs' },
    })
      .then(auth.signOut)
      .catch(() => {
        //
      });
  };

  const [showModalMinhasEquipes, hideModalMinhasEquipes] = useModal(
    ({ in: open }) => (
      <ModalMinhasEquipes open={open} onClose={hideModalMinhasEquipes} />
    ),
  );

  const [showModalAlterarSenha, hideModalAlterarSenha] = useModal(
    ({ in: open }) => (
      <ModalAlterarSenha open={open} onClose={hideModalAlterarSenha} />
    ),
  );

  return (
    <>
      <ButtonBase disableRipple {...bindTrigger(popupState)}>
        <Avatar src={currentUser?.foto} />
      </ButtonBase>

      <Popover
        {...bindPopover(popupState)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        classes={{ paper: classes.popover }}
      >
        <Box
          aria-label="User area"
          display="flex"
          alignItems="center"
          ml={2}
          mr={2}
          mt={1}
          gridGap={8}
        >
          <Box flexShrink={0}>
            <UploadAvatarProfile />
          </Box>

          <Box>
            <Typography
              variant="body1"
              noWrap
              style={{ width: 200, fontWeight: 500 }}
            >
              {currentUser?.apelido || currentUser?.nome}
            </Typography>
            <Typography
              style={{ width: 200, marginBottom: 4 }}
              noWrap
              paragraph
              variant="caption"
              color="textSecondary"
            >
              {currentUser?.email}
            </Typography>

            <Tooltip
              arrow
              placement="left"
              title={
                <ListItemText
                  style={{ padding: 0 }}
                  primary="Média dos últimos 30 dias"
                  secondary={`${totalAvaliacoes} ${
                    totalAvaliacoes > 1 ? 'avaliações' : 'avaliação'
                  }`}
                  secondaryTypographyProps={{
                    noWrap: true,
                    color: 'inherit',
                  }}
                />
              }
            >
              <Box display="inline-block">
                <Rating
                  readOnly
                  size="small"
                  name="avaliacoes"
                  precision={0.5}
                  defaultValue={currentUser?.avaliacoes?.nota_media}
                />
              </Box>
            </Tooltip>
          </Box>
        </Box>

        <ToggleDistribuicaoAutomaticaFila />

        <Divider
          style={{
            marginLeft: -8,
            marginRight: -8,
            marginTop: 8,
            marginBottom: 8,
          }}
        />

        <List style={{ marginLeft: 8, marginRight: 8 }}>
          <ListItem style={{ padding: '4px 8px' }}>
            <ListItemIcon style={{ minWidth: 28 }}>
              <FontDownloadOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primaryTypographyProps={{ variant: 'body2' }}
              primary="Tamanho da fonte"
            />
            <FontResize />
          </ListItem>

          <ListItem style={{ padding: '4px 8px' }}>
            <ListItemIcon style={{ minWidth: 28 }}>
              <Brightness7Icon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primaryTypographyProps={{ variant: 'body2' }}
              primary="Modo escuro"
            />
            <ToggleTheme />
          </ListItem>

          <Divider
            style={{
              marginLeft: -8,
              marginRight: -8,
              marginTop: 8,
              marginBottom: 8,
            }}
          />

          <ListItem button onClick={showModalMinhasEquipes}>
            <ListItemIcon style={{ minWidth: 28 }}>
              <GroupIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primaryTypographyProps={{ variant: 'body2' }}
              primary="Ver minhas equipes"
            />
          </ListItem>

          <ListItem button onClick={showModalAlterarSenha}>
            <ListItemIcon style={{ minWidth: 28 }}>
              <LockIcon fontSize="small" />
            </ListItemIcon>

            <ListItemText
              primaryTypographyProps={{ variant: 'body2' }}
              primary="Alterar minha senha"
            />
          </ListItem>

          <Divider
            style={{
              marginLeft: -8,
              marginRight: -8,
              marginTop: 8,
              marginBottom: 8,
            }}
          />

          <ListItem button onClick={handleConfirmSignOut}>
            <ListItemIcon style={{ minWidth: 28 }}>
              <ExitToAppIcon fontSize="small" />
            </ListItemIcon>

            <ListItemText
              primaryTypographyProps={{ variant: 'body2' }}
              primary="Sair"
            />
          </ListItem>
        </List>
      </Popover>
    </>
  );
}
